import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import type {
  ActionStep,
  DeviceInfo,
  GameState,
  Language,
  ServerMessage,
  StepStatus,
} from '@elena/shared';
import { api, ApiError, type ServerStatus } from './lib/api';
import { session } from './lib/session';
import { ElenaSocket, type ConnectionState } from './lib/socket';
import { setSpeechLanguage, speakFully, stopSpeaking } from './lib/speech';

export interface ChatEntry {
  id: string;
  role: 'user' | 'elena';
  text: string;
  at: string;
  steps?: ActionStep[];
}

export interface ActivityEntry {
  id: string;
  at: string;
  summary: string;
  origin: string;
  status: StepStatus;
}

export interface ConfirmEntry {
  confirmId: string;
  summary: string;
  expiresAt: string;
}

interface State {
  connection: ConnectionState;
  devices: DeviceInfo[];
  messages: ChatEntry[];
  activity: ActivityEntry[];
  confirms: ConfirmEntry[];
  thinking: boolean;
  status: ServerStatus | null;
  error: string | null;
  /** The game running on this device, pushed by the server. */
  game: GameState | null;
  /** The conversation language from the server; the mic and "Hej Elena" listen in it. */
  language: Language;
  /** Hands-free conversation mode. */
  talk: boolean;
  /** Last reply Elena finished saying out loud (talk mode listens after it). */
  spokenTurn: string | null;
}

type Event =
  | { type: 'connection'; state: ConnectionState }
  | { type: 'server'; msg: ServerMessage }
  | { type: 'user'; entry: ChatEntry }
  | { type: 'status'; status: ServerStatus }
  | { type: 'confirmed'; confirmId: string }
  | { type: 'error'; message: string | null }
  | { type: 'talk'; on: boolean }
  | { type: 'spoken'; turnId: string };

const initial: State = {
  connection: 'connecting',
  devices: [],
  messages: [],
  activity: [],
  confirms: [],
  thinking: false,
  status: null,
  error: null,
  game: null,
  language: 'sr',
  talk: false,
  spokenTurn: null,
};

const uid = () => crypto.randomUUID();

function reducer(state: State, event: Event): State {
  switch (event.type) {
    case 'connection':
      return { ...state, connection: event.state };
    case 'user':
      return { ...state, messages: [...state.messages, event.entry].slice(-200), thinking: true };
    case 'status':
      return { ...state, status: event.status, devices: event.status.devices };
    case 'confirmed':
      return { ...state, confirms: state.confirms.filter((c) => c.confirmId !== event.confirmId) };
    case 'error':
      return { ...state, error: event.message, thinking: event.message ? false : state.thinking };
    case 'talk':
      return { ...state, talk: event.on };
    case 'spoken':
      return { ...state, spokenTurn: event.turnId };
    case 'server': {
      const msg = event.msg;
      switch (msg.type) {
        case 'devices':
          return { ...state, devices: msg.devices };
        case 'thinking':
          return { ...state, thinking: true };
        case 'assistant':
          return {
            ...state,
            thinking: false,
            talk:
              msg.conversation === 'start' ? true : msg.conversation === 'end' ? false : state.talk,
            messages: [
              ...state.messages,
              {
                id: msg.turnId,
                role: 'elena' as const,
                text: msg.text,
                steps: msg.steps,
                at: new Date().toISOString(),
              },
            ].slice(-200),
          };
        case 'activity':
          return {
            ...state,
            activity: [
              {
                id: uid(),
                at: msg.at,
                summary: msg.summary,
                origin: msg.origin,
                status: msg.status,
              },
              ...state.activity,
            ].slice(0, 50),
          };
        case 'game':
          return { ...state, game: msg.state };
        case 'welcome':
          return msg.language ? { ...state, language: msg.language } : state;
        case 'language':
          return { ...state, language: msg.language };
        case 'confirm_request':
          return { ...state, confirms: [...state.confirms, msg] };
        case 'error':
          return { ...state, error: msg.message, thinking: false };
        default:
          return state;
      }
    }
  }
}

interface ElenaContext extends State {
  send: (text: string, spoken: boolean, alternatives?: string[]) => void;
  /** Starts ("hajde da pričamo") or ends hands-free conversation mode. */
  setTalk: (on: boolean) => void;
  answer: (confirmId: string, approve: boolean) => void;
  refresh: () => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<ElenaContext | null>(null);

export function ElenaProvider({
  children,
  onUnauthorized,
}: {
  children: ReactNode;
  onUnauthorized: () => void;
}) {
  const [state, dispatch] = useReducer(reducer, initial);
  const socket = useRef<ElenaSocket | null>(null);
  const lastSpoken = useRef(false);
  const voiceEnabled = useRef(false);
  const talkRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const status = await api.status();
      voiceEnabled.current = status.voice.enabled;
      dispatch({ type: 'status', status });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) onUnauthorized();
    }
  }, [onUnauthorized]);

  useEffect(() => {
    const s = new ElenaSocket(
      (msg) => {
        dispatch({ type: 'server', msg });
        if (msg.type !== 'assistant') return;
        const talking = talkRef.current || msg.conversation === 'start';
        if (msg.conversation) talkRef.current = msg.conversation === 'start';
        if (talking || lastSpoken.current || session.speakReplies()) {
          void speakFully(msg.text, voiceEnabled.current).then(() =>
            dispatch({ type: 'spoken', turnId: msg.turnId }),
          );
        } else {
          dispatch({ type: 'spoken', turnId: msg.turnId });
        }
      },
      (connection) => dispatch({ type: 'connection', state: connection }),
    );
    socket.current = s;
    s.connect();
    void refresh();
    const timer = window.setInterval(() => void refresh(), 20_000);
    return () => {
      window.clearInterval(timer);
      s.close();
      stopSpeaking();
    };
  }, [refresh]);

  const send = useCallback((text: string, spoken: boolean, alternatives?: string[]) => {
    const clean = text.trim();
    if (!clean) return;
    lastSpoken.current = spoken;
    stopSpeaking();
    const ok = socket.current?.send({
      type: 'chat',
      text: clean,
      origin: session.origin(),
      spoken,
      conversation: talkRef.current,
      ...(spoken && alternatives?.length ? { alternatives: alternatives.slice(0, 3) } : {}),
    });
    if (!ok) {
      dispatch({ type: 'error', message: 'Nisam povezana sa serverom. Pokušavam ponovo…' });
      return;
    }
    dispatch({ type: 'error', message: null });
    dispatch({
      type: 'user',
      entry: { id: uid(), role: 'user', text: clean, at: new Date().toISOString() },
    });
  }, []);

  const setTalk = useCallback(
    (on: boolean) => {
      if (on) {
        if (!talkRef.current) send('Hajde da pričamo', true);
        return;
      }
      talkRef.current = false;
      stopSpeaking();
      dispatch({ type: 'talk', on: false });
    },
    [send],
  );

  const answer = useCallback((confirmId: string, approve: boolean) => {
    socket.current?.send({ type: 'confirm_reply', confirmId, approve });
    dispatch({ type: 'confirmed', confirmId });
  }, []);

  const logout = useCallback(() => {
    socket.current?.close();
    session.setToken(null);
    onUnauthorized();
  }, [onUnauthorized]);

  useEffect(() => setSpeechLanguage(state.language), [state.language]);

  const value = useMemo(
    () => ({ ...state, send, setTalk, answer, refresh, logout }),
    [state, send, setTalk, answer, refresh, logout],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useElena(): ElenaContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useElena must be used inside ElenaProvider');
  return ctx;
}
